import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle } from 'lucide-react';
import Footer from '../components/Footer';

const LegalPage = () => {
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract page type from URL path
  const getPageType = () => {
    const path = location.pathname;
    if (path.includes('/privacy') || path.includes('/datenschutz')) return 'privacy';
    if (path.includes('/terms') || path.includes('/agb')) return 'terms';
    if (path.includes('/cookies')) return 'cookies';
    if (path.includes('/dpa')) return 'dpa';
    if (path.includes('/imprint') || path.includes('/impressum')) return 'imprint';
    return 'privacy';
  };

  useEffect(() => {
    fetchLegalContent();
  }, [location.pathname]);

  const fetchLegalContent = async () => {
    setLoading(true);
    setError(null);
    
    const pageType = getPageType();
    
    try {
      const response = await fetch(`/api/legal/${pageType}`);
      
      if (!response.ok) {
        throw new Error('Failed to load legal content');
      }
      
      const data = await response.json();
      setContent(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-8">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/4 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-red-200 bg-red-50 dark:bg-red-950">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-900 dark:text-red-100">
                <AlertTriangle size={24} />
                <div>
                  <div className="font-semibold">Fehler beim Laden</div>
                  <div className="text-sm">{error}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex flex-col">
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-lg">
            <CardContent className="pt-8 pb-8">
              {/* Header */}
              <div className="mb-8 pb-6 border-b">
                <h1 className="text-4xl font-bold mb-2" data-testid="legal-page-title">
                  {content?.title}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Letzte Aktualisierung: {content?.last_updated}
                </p>
              </div>

              {/* Content */}
              <div 
                className="prose prose-slate dark:prose-invert max-w-none
                           prose-headings:font-bold prose-headings:text-slate-900 dark:prose-headings:text-slate-100
                           prose-h1:text-3xl prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                           prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                           prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-p:leading-relaxed
                           prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
                           prose-strong:text-slate-900 dark:prose-strong:text-slate-100
                           prose-ul:my-4 prose-li:my-1
                           prose-table:border prose-table:border-slate-300 dark:prose-table:border-slate-700
                           prose-th:bg-slate-100 dark:prose-th:bg-slate-800 prose-th:p-3
                           prose-td:p-3 prose-td:border-t prose-td:border-slate-200 dark:prose-td:border-slate-700"
                data-testid="legal-page-content"
              >
                <ReactMarkdown>{content?.content}</ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LegalPage;
